import {
  type CreateEndpointProps,
  type EndpointManagedType,
  buildSslProtocolsOptions,
  endpointSettingApplies,
  fetchApp,
  fetchEnvironmentById,
  fetchImageById,
  fetchStack,
  getContainerPort,
  loadBalancingAlgorithms,
  parseIpStr,
  provisionEndpoint,
  selectAppById,
  selectEnvironmentById,
  selectImageById,
  selectStackById,
} from "@app/deploy";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import { endpointDetailUrl } from "@app/routes";
import type { EndpointType } from "@app/types";
import { existValidator, ipValidator, portValidator } from "@app/validator";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useValidator } from "../hooks";
import {
  BannerMessages,
  Box,
  ButtonCreate,
  ButtonLinkDocs,
  CertSelector,
  CheckBox,
  CreateAppEndpointSelector,
  EndpointHealthchecksSettings,
  EndpointSecuritySettings,
  FileDropTextArea,
  Form,
  FormGroup,
  Input,
  Radio,
  RadioGroup,
  Select,
  type SelectOption,
} from "../shared";

const loadBalancingAlgorithmOptions: SelectOption[] = Object.keys(
  loadBalancingAlgorithms,
).map((alg) => ({ label: alg, value: loadBalancingAlgorithms[alg] }));

const validators = {
  port: (data: CreateEndpointProps) => portValidator(data.containerPort),
  ipAllowlist: (data: CreateEndpointProps) => ipValidator(data.ipAllowlist),
  service: (data: CreateEndpointProps) =>
    existValidator(data.serviceId, "Must select a service"),
  cert: (data: CreateEndpointProps) => {
    if (data.type !== "custom") return;
    if (data.certId === "" && data.cert === "") {
      return "A certificate is required for custom certificate";
    }
  },
  privKey: (data: CreateEndpointProps) => {
    if (data.type !== "custom") return;
    if (data.certId === "" && data.privKey === "") {
      return "A private key is required for custom certificate";
    }
  },
  tokenHeader: (data: CreateEndpointProps) => {
    if (data.trafficType === "tcp" || data.trafficType === "tls") {
      return "A HTTP or GRPC endpoint is required to pass token";
    }
  },
  domain: (data: CreateEndpointProps) => {
    if (data.type === "managed") {
      if (data.domain === "") {
        return "A domain is required for managed certificate";
      }
    }
    if (data.type === "custom" && data.shared) {
      if (!data.domain || data.domain === "") {
        return "A domain is required for shared endpoints with a custom certificate";
      }
      if (data.domain.includes("*")) {
        return "Shared endpoints do not support wildcard domains. Please use a specific domain name.";
      }
    }
  },
};

export const AppCreateEndpointPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id = "" } = useParams();
  useQuery(fetchApp({ id }));
  const app = useSelector((s) => selectAppById(s, { id }));
  useQuery(fetchImageById({ id: app.currentImageId }));
  const image = useSelector((s) =>
    selectImageById(s, { id: app.currentImageId }),
  );
  useQuery(fetchEnvironmentById({ id: app.environmentId }));
  const env = useSelector((s) =>
    selectEnvironmentById(s, { id: app.environmentId }),
  );
  useQuery(fetchStack({ id: env.stackId }));
  const stack = useSelector((s) => selectStackById(s, { id: env.stackId }));

  const [serviceId, setServiceId] = useState("");
  const [port, setPort] = useState("");
  const [trType, setTrType] = useState<EndpointType>("http");
  const [enpType, setEnpType] = useState<EndpointManagedType>("default");
  const [enpPlacement, setEnpPlacement] = useState("external");
  const [ipAllowlist, setIpAllowlist] = useState("");
  const [domain, setDomain] = useState("");
  const [transCert, setTransCert] = useState(false);
  const [cert, setCert] = useState("");
  const [certId, setCertId] = useState("");
  const [privKey, setPrivKey] = useState("");
  const [tokenHeader, setTokenHeader] = useState(
    undefined as string | undefined,
  );
  const [lbAlgorithm, setLbAlgorithm] = useState("round_robin");
  const [shared, setShared] = useState(false);
  const [idleTimeout, setIdleTimeout] = useState("");
  const [forceSSL, setForceSSL] = useState("");
  const [maintenancePageUrl, setMaintenancePageUrl] = useState("");
  const [strictHealthChecks, setStrictHealthChecks] = useState("");
  const [sslProtocolsOverride, setSslProtocolsOverride] = useState("");
  const [sslCiphersOverride, setSslCiphersOverride] = useState("");
  const [disableWeakCipherSuites, setDisableWeakCipherSuites] = useState("");
  const [showElbHealthchecks, setShowElbHealthchecks] = useState("");
  const [releaseHealthcheckTimeout, setReleaseHealthcheckTimeout] =
    useState("");
  const portText = getContainerPort(
    { containerPort: port, containerPorts: [] },
    image.exposedPorts,
  );
  const [usingNewCert, setUsingNewCert] = useState(false);
  const isHttp = trType === "http" || trType === "http_proxy_protocol";
  const applies = (setting: string) =>
    endpointSettingApplies(setting, trType, "alb");
  const maintenancePageUrlError = (() => {
    if (maintenancePageUrl === "") return undefined;
    try {
      const u = new URL(maintenancePageUrl);
      if (u.protocol !== "http:" && u.protocol !== "https:") {
        return "Must be an http or https URL";
      }
    } catch {
      return "Must be a valid URL";
    }
  })();
  const releaseHealthcheckTimeoutError = (() => {
    if (releaseHealthcheckTimeout === "") return undefined;
    const n = Number(releaseHealthcheckTimeout);
    if (!Number.isInteger(n) || n < 1 || n > 15 * 60) {
      return "Must be between 1 and 900 seconds";
    }
  })();
  const idleTimeoutError = (() => {
    if (idleTimeout === "") return undefined;
    const n = Number(idleTimeout);
    if (!Number.isInteger(n) || n < 30 || n > 2400) {
      return "Must be between 30 and 2400 seconds";
    }
  })();

  const createData = (): CreateEndpointProps => {
    const settings: Record<string, string> = {
      ...(applies("FORCE_SSL") && forceSSL !== ""
        ? { FORCE_SSL: forceSSL }
        : {}),
      ...(applies("IDLE_TIMEOUT") && idleTimeout !== ""
        ? { IDLE_TIMEOUT: idleTimeout }
        : {}),
      ...(applies("MAINTENANCE_PAGE_URL") && maintenancePageUrl !== ""
        ? { MAINTENANCE_PAGE_URL: maintenancePageUrl }
        : {}),
      ...(applies("STRICT_HEALTH_CHECKS") && strictHealthChecks !== ""
        ? { STRICT_HEALTH_CHECKS: strictHealthChecks }
        : {}),
      ...(applies("SSL_PROTOCOLS_OVERRIDE") && sslProtocolsOverride !== ""
        ? { SSL_PROTOCOLS_OVERRIDE: sslProtocolsOverride }
        : {}),
      ...(applies("SSL_CIPHERS_OVERRIDE") && sslCiphersOverride !== ""
        ? { SSL_CIPHERS_OVERRIDE: sslCiphersOverride }
        : {}),
      ...(applies("DISABLE_WEAK_CIPHER_SUITES") &&
      disableWeakCipherSuites !== ""
        ? { DISABLE_WEAK_CIPHER_SUITES: disableWeakCipherSuites }
        : {}),
      ...(applies("SHOW_ELB_HEALTHCHECKS") && showElbHealthchecks !== ""
        ? { SHOW_ELB_HEALTHCHECKS: showElbHealthchecks }
        : {}),
      ...(applies("RELEASE_HEALTHCHECK_TIMEOUT") &&
      releaseHealthcheckTimeout !== ""
        ? { RELEASE_HEALTHCHECK_TIMEOUT: releaseHealthcheckTimeout }
        : {}),
    };

    const def = {
      serviceId,
      trafficType: trType,
      internal: enpPlacement === "internal",
      ipAllowlist: parseIpStr(ipAllowlist),
      containerPort: port,
      tokenHeader: isHttp ? tokenHeader : undefined,
      lbAlgorithm: isHttp ? lbAlgorithm : undefined,
      shared: isHttp ? shared : false,
      settings,
    };

    if (enpType === "managed") {
      return {
        type: "managed",
        envId: app.environmentId,
        certId: usingNewCert ? "" : certId,
        cert,
        privKey,
        domain,
        ...def,
      };
    }

    if (enpType === "custom") {
      return {
        type: "custom",
        envId: app.environmentId,
        certId: usingNewCert ? "" : certId,
        cert,
        privKey,
        domain: isHttp && shared ? domain : undefined,
        ...def,
      };
    }

    return {
      type: "default",
      envId: app.environmentId,
      ...def,
    };
  };

  const [errors, validate] = useValidator<
    CreateEndpointProps,
    typeof validators
  >(validators);
  const formData = createData();
  const action = provisionEndpoint(formData);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate(formData)) return;
    if (idleTimeoutError) return;
    if (releaseHealthcheckTimeoutError) return;
    if (maintenancePageUrlError) return;
    dispatch(action);
  };

  const loader = useLoader(action);
  useLoaderSuccess(loader, () => {
    navigate(endpointDetailUrl(loader.meta.endpointId));
  });

  const options: SelectOption[] = [
    {
      label: `Use app-${app.id}.${stack.defaultDomain} default endpoint`,
      value: "default",
    },
    { label: "Use a custom domain with Managed certificate", value: "managed" },
    { label: "Use a custom domain with a custom certificate", value: "custom" },
  ];

  let domainDescription =
    "You will need to be able to create a CNAME from this domain name to one provided by Aptible. Wildcard domains are supported.";
  if (isHttp && shared) {
    domainDescription =
      "You will need to be able to create a CNAME from this domain name to one provided by Aptible. Wildcard domains are not supported.";
  }
  const domainForm = (
    <FormGroup
      label="Domain Name"
      htmlFor="domain"
      description={domainDescription}
      feedbackMessage={errors.domain}
      feedbackVariant={errors.domain ? "danger" : "info"}
    >
      <Input
        type="text"
        name="domain"
        value={domain}
        onChange={(e) => setDomain(e.currentTarget.value)}
      />
    </FormGroup>
  );

  const transCertForm = (
    <FormGroup
      label="Transitional Certificate"
      htmlFor="trans-cert"
      description="Managed certificate provisions a certificate for you, but this process takes a little while. During this time, your application will be unavailable. If you need to avoid downtime, you can provide a transitional certificate, which will let Aptible provision your Managed certificate in the background, while your app is running."
    >
      <CheckBox
        label="Use a transitional certificate (recommended if your application is already live)"
        name="trans-cert"
        checked={transCert}
        onChange={(e) => setTransCert(e.currentTarget.checked)}
      />
    </FormGroup>
  );

  const certSelectorForm = (
    <FormGroup
      htmlFor="existing-cert"
      label="Certificate"
      feedbackMessage={errors.cert}
      feedbackVariant={errors.cert ? "danger" : "info"}
    >
      {usingNewCert ? null : (
        <CertSelector
          envId={app.environmentId}
          selectedId={certId}
          onSelect={(opt) => setCertId(opt.value)}
          className="mb-2"
        />
      )}
      <CheckBox
        label="Create a New Certificate"
        name="new-cert"
        checked={usingNewCert}
        onChange={(e) => setUsingNewCert(e.currentTarget.checked)}
      />
    </FormGroup>
  );

  const certForm = (
    <FormGroup
      htmlFor="cert"
      label="New Certificate"
      feedbackMessage={errors.cert}
      feedbackVariant={errors.cert ? "danger" : "info"}
      description={
        <>
          <p>
            Drag and drop your certificate file or paste its contents in the
            textarea below.
          </p>
          <p>
            If you have a bundled certificate chain, drag and drop all of the
            certificate files or paste their contents in the textarea below.
          </p>
        </>
      }
    >
      <FileDropTextArea
        id="cert"
        aria-label="cert"
        onValueChange={setCert}
        value={cert}
      />
    </FormGroup>
  );

  const privKeyForm = (
    <FormGroup
      htmlFor="private-key"
      label="Private Key for New Certificate"
      description="Drag and drop your private key file or paste its contents in the textarea below."
      feedbackMessage={errors.privKey}
      feedbackVariant={errors.privKey ? "danger" : "info"}
    >
      <FileDropTextArea
        id="private-key"
        aria-label="private-key"
        onValueChange={setPrivKey}
        value={privKey}
      />
    </FormGroup>
  );

  const enpPlacementForm = (
    <FormGroup label="Endpoint Placement" htmlFor="endpoint-placement">
      <RadioGroup
        name="endpoint-placement"
        selected={enpPlacement}
        onSelect={setEnpPlacement}
      >
        <Radio value="external">External</Radio>
        <Radio value="internal">Internal</Radio>
      </RadioGroup>
    </FormGroup>
  );

  const tokenHeaderForm = (
    <FormGroup
      label="Header Authentication Value"
      htmlFor="token-header"
      description={`The 'X-Origin-Token' header value. When set, clients will be required to pass a
        'X-Origin-Token' header matching this value. Token header may only contain letters, numbers,
        '_', '-', ':', and '.'`}
      feedbackMessage={errors.tokenHeader}
      feedbackVariant={errors.tokenHeader ? "danger" : "info"}
    >
      <Input
        id="token-header"
        name="token-header"
        type="text"
        value={tokenHeader}
        onChange={(e) => setTokenHeader(e.currentTarget.value)}
      />
    </FormGroup>
  );

  const lbAlgorithmForm = (
    <FormGroup
      label="Load Balancing Algorithm"
      htmlFor="lb-algorithm"
      description="Choose the load balancing algorithm for ALB endpoints. Only applies to ALB endpoints."
    >
      <Select
        ariaLabel="Load Balancing Algorithm"
        id="lb-algorithm"
        value={lbAlgorithm}
        options={loadBalancingAlgorithmOptions}
        onSelect={(opt) => setLbAlgorithm(opt.value)}
      />
    </FormGroup>
  );

  const sharedForm = (
    <FormGroup
      label="Shared Endpoint"
      htmlFor="shared"
      description="When enabled, the underlying load balancer will be shared by other shared endpoints, reducing costs."
      feedbackMessage={
        shared
          ? "Warning: Shared endpoints cannot be converted to dedicated endpoints."
          : undefined
      }
      feedbackVariant="danger"
    >
      <CheckBox
        label="Enable shared endpoint"
        name="shared"
        checked={shared}
        onChange={(e) => setShared(e.currentTarget.checked)}
      />
    </FormGroup>
  );

  const securityForm = (
    <EndpointSecuritySettings
      applies={applies}
      sslProtocolsOptions={buildSslProtocolsOptions(isHttp)}
      forceSSL={forceSSL}
      onForceSSLChange={setForceSSL}
      sslProtocolsOverride={sslProtocolsOverride}
      onSslProtocolsOverrideChange={setSslProtocolsOverride}
      sslCiphersOverride={sslCiphersOverride}
      onSslCiphersOverrideChange={setSslCiphersOverride}
      disableWeakCipherSuites={disableWeakCipherSuites}
      onDisableWeakCipherSuitesChange={setDisableWeakCipherSuites}
      ipAllowlist={ipAllowlist}
      onIpAllowlistChange={setIpAllowlist}
      ipAllowlistError={errors.ipAllowlist}
      tokenFormSlot={isHttp ? tokenHeaderForm : null}
    />
  );

  const healthchecksForm = (
    <EndpointHealthchecksSettings
      applies={applies}
      strictHealthChecks={strictHealthChecks}
      onStrictHealthChecksChange={setStrictHealthChecks}
      releaseHealthcheckTimeout={releaseHealthcheckTimeout}
      onReleaseHealthcheckTimeoutChange={setReleaseHealthcheckTimeout}
      releaseHealthcheckTimeoutError={releaseHealthcheckTimeoutError}
      idleTimeout={idleTimeout}
      onIdleTimeoutChange={setIdleTimeout}
      idleTimeoutError={idleTimeoutError}
      showElbHealthchecks={showElbHealthchecks}
      onShowElbHealthchecksChange={setShowElbHealthchecks}
      maintenancePageUrl={maintenancePageUrl}
      onMaintenancePageUrlChange={setMaintenancePageUrl}
      maintenancePageUrlError={maintenancePageUrlError}
      lbAlgorithmSlot={isHttp ? lbAlgorithmForm : null}
    />
  );

  const getProtocolName = (trafficType: EndpointType) => {
    switch (trafficType) {
      case "grpc":
        return "gRPC";
      case "http_proxy_protocol":
        return "HTTP";
      case "http":
        return "HTTP";
      default:
        return "HTTP";
    }
  };

  const form = () => {
    if (enpType === "managed") {
      return (
        <>
          {enpPlacementForm}
          {domainForm}
          {transCertForm}
          {transCert ? certSelectorForm : null}
          {transCert && usingNewCert ? certForm : null}
          {transCert && usingNewCert ? privKeyForm : null}
          {isHttp ? sharedForm : null}
        </>
      );
    }

    if (enpType === "custom") {
      return (
        <>
          {enpPlacementForm}
          {certSelectorForm}
          {usingNewCert ? certForm : null}
          {usingNewCert ? privKeyForm : null}
          {isHttp ? sharedForm : null}
          {isHttp && shared ? domainForm : null}
        </>
      );
    }
    return (
      <>
        {enpPlacementForm}
        {isHttp ? sharedForm : null}
      </>
    );
  };

  return (
    <Box>
      <div className="flex justify-between items-start">
        <h1 className="text-lg text-black font-semibold">Create Endpoint</h1>
        <ButtonLinkDocs href="https://www.aptible.com/docs/core-concepts/apps/connecting-to-apps/app-endpoints/overview" />
      </div>
      <div className="mt-2 mb-4 text-black-900">
        This Endpoint will accept{" "}
        {getProtocolName(trType) === "HTTP"
          ? "HTTP and HTTPS"
          : getProtocolName(trType)}{" "}
        traffic and route it to your app over {getProtocolName(trType)}.{" "}
        {getProtocolName(trType) === "HTTP"
          ? "Additionally, you can set FORCE_SSL=true in your App configuration to route all traffic as HTTPS."
          : ""}
      </div>
      <Form onSubmit={onSubmit} className="max-w-4xl">
        <FormGroup label="Traffic Type" htmlFor="traffic-type">
          <RadioGroup
            name="traffic-type"
            selected={trType}
            onSelect={setTrType}
          >
            <Radio value="http">HTTP</Radio>
            <Radio value="grpc">gRPC</Radio>
          </RadioGroup>
        </FormGroup>
        <FormGroup
          label="Service"
          htmlFor="service"
          feedbackMessage={errors.service}
          feedbackVariant={errors.service ? "danger" : "info"}
        >
          <CreateAppEndpointSelector
            app={app}
            selectedId={serviceId}
            onSelect={setServiceId}
          />
        </FormGroup>

        <FormGroup
          label="Custom Container Port"
          htmlFor="port"
          description={`Aptible will deliver ${getProtocolName(trType)} traffic to your app on port (${portText}).`}
          feedbackMessage={errors.port}
          feedbackVariant={errors.port ? "danger" : "info"}
        >
          <Input
            id="port"
            name="port"
            type="text"
            value={port}
            onChange={(e) => setPort(e.currentTarget.value)}
          />
          <div className="text-base text-black-500 pt-2">
            Choose any port number between 1 and 65535. Your app must be
            listening for {getProtocolName(trType)} traffic on this port, and it
            must be exposed by your Docker image.
          </div>
        </FormGroup>

        <FormGroup label="Endpoint Type" htmlFor="endpoint-type">
          <Select
            ariaLabel="Endpoint Type"
            id="endpoint-type"
            options={options}
            onSelect={(opt) => setEnpType(opt.value as EndpointManagedType)}
            value={enpType}
          />
        </FormGroup>

        {form()}

        <div className="border-y border-gray-200 divide-y divide-gray-200">
          {securityForm}
          {healthchecksForm}
        </div>

        <BannerMessages {...loader} />

        <ButtonCreate
          envId={app.environmentId}
          isLoading={loader.isLoading}
          type="submit"
          className="w-[200px]"
        >
          Save Endpoint
        </ButtonCreate>
      </Form>
    </Box>
  );
};
